import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import EcommerceUtils from '~/components/ecommerce/ecommerce-utils';
import { getCookie } from '~/common';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { AcademyBreadcrumb } from '~/features/academies/components/AcademyBreadcrumb/AcademyBreadcrumb';
import AcademyThemeBanner from './academy-theme-banner';
import AcademyThemeFooter from './academy-theme-footer';
import AcademyThemeNavigation from './academy-theme-navigation';
import AcademyThemeSubMenu from './academy-theme-sub-menu/academy-theme-sub-menu';
import AcademyThemeWrapper from './academy-theme-wrapper';

export const doesUserNeedsToPurchaseContent = (purchasableContentInfo) => {
  const { contentId, accessibleContentIds, amountCents, amountProprietaryCurrency } = purchasableContentInfo;
  return EcommerceUtils.userNeedsToPurchase(contentId, accessibleContentIds, amountCents, amountProprietaryCurrency);
};

const withAcademyTheme = (WrappedComponent) =>
  class extends Component {
    render() {
      const { academy, adminUser, customThemeOptions, academyTemplate } = this.props;
      if (!academy) return <WrappedComponent {...this.props} />;

      const {
        certificationsEnabled,
        eventsEnabled,
        themeOptions,
        pathsEnabled,
        collectionsEnabled,
        academyCustomPages = [],
      } = academy;
      const customThemeOptionsBanner = customThemeOptions.banner || null;
      const customThemeOptionsBreadCrumbs = customThemeOptions.breadCrumbs || null;
      const customThemeOptionsCurrentSubMenu = customThemeOptions.currentSubMenu || null;
      const customThemeOptionsNavigation = customThemeOptions.navigation || null;
      const customThemeOptionsWrapper = customThemeOptions.wrapper || null;
      const {
        academyContentTypesToLocalizedString,
        academySearchableContentTypes,
        communityContentTypesToLocalizedString,
      } = customThemeOptions;

      const currentUser = getExternalUser();
      const userLanguageCode = adminUser
        ? academy.language
        : (currentUser && currentUser.language) ||
          getCookie(APP_CONSTANTS.USER_LANGUAGE_COOKIE) ||
          academy.language ||
          APP_CONSTANTS.DEFAULT_LANGUAGE;

      const showSearch =
        (isFeatureEnabled('academy_search') || (adminUser && adminUser.enterprise.feature_flips.academy_search)) &&
        themeOptions.showSearch;
      const { showFilters } = themeOptions;

      const showMyTrainingsNavigation =
        (currentUser || adminUser) &&
        themeOptions.showMyTrainingsTab &&
        (isFeatureEnabled('academies_assignments') ||
          (adminUser && adminUser.enterprise.feature_flips.academies_assignments));
      const showPathsNavigation =
        (isFeatureEnabled('academy_paths') || (adminUser && adminUser.enterprise.feature_flips.academy_paths)) &&
        pathsEnabled;

      const customButtonProps =
        customThemeOptionsBanner && customThemeOptionsBanner.button && _.isObject(customThemeOptionsBanner.button)
          ? customThemeOptionsBanner.button
          : {};

      const { academyContent } = this.props;

      const contentManagerEnabled = isFeatureEnabled('fy25_q1_clc_content_manager');

      const getBreadcrumbLabel = (keyOrTitle) => {
        const { ACADEMY_SUB_MENU_LINKS_KEY_MAP } = APP_CONSTANTS;
        if (Object.values(ACADEMY_SUB_MENU_LINKS_KEY_MAP).includes(keyOrTitle)) {
          return (
            (contentManagerEnabled
              ? themeOptions.tabTitles[keyOrTitle]
              : themeOptions.tabTitles[keyOrTitle] || themeOptions.contentLabelOverrides[keyOrTitle]) ||
            I18n.t(`common.academy.content.${keyOrTitle}`)
          );
        }

        return keyOrTitle;
      };

      const breadcrumbLabel = getBreadcrumbLabel(
        customThemeOptionsCurrentSubMenu?.KEY ?? customThemeOptionsBanner?.title,
      );

      return (
        <div
          className={`${academyCustomCSS.templates.common} ${
            academyTemplate && academyCustomCSS.templates[academyTemplate]
          }`}
        >
          <AcademyThemeWrapper
            hasLogo={academy.logo}
            style={
              customThemeOptionsWrapper &&
              customThemeOptionsWrapper.style &&
              _.isObject(customThemeOptionsWrapper.style)
                ? customThemeOptionsWrapper.style
                : {}
            }
          >
            <SkipToMainLink href="#main">{I18n.t('js.academy.layout.skip_to_link')}</SkipToMainLink>
            <AcademyThemeNavigation
              academy={academy}
              academyContentTypesToLocalizedString={academyContentTypesToLocalizedString}
              academySearchableContentTypes={academySearchableContentTypes}
              backToLink={themeOptions.backToLink}
              backToLinkText={themeOptions.backToLinkText}
              certificationsEnabled={certificationsEnabled}
              communityContentTypesToLocalizedString={communityContentTypesToLocalizedString}
              pathsEnabled={pathsEnabled}
              contentWrapperStyle={
                customThemeOptionsNavigation &&
                customThemeOptionsNavigation.contentWrapperStyle &&
                _.isObject(customThemeOptionsNavigation.contentWrapperStyle)
                  ? customThemeOptionsNavigation.contentWrapperStyle
                  : {}
              }
              eventsEnabled={eventsEnabled}
              headerLinks={themeOptions.headerLinks}
              homepageLink="/academy"
              linkColor={themeOptions.titleColor}
              linkHoverColor={themeOptions.titleColor}
              logo={academy.logo}
              logoHeight={themeOptions.logoHeight}
              showBackToLink={themeOptions.showBackToLink}
              showSearch={showSearch}
              subnavActiveLinkColor={
                customThemeOptionsBanner && customThemeOptionsBanner.subnavActiveLinkColor
                  ? customThemeOptionsBanner.subnavActiveLinkColor
                  : themeOptions.subnavActiveLinkColor
              }
            />
            <main id="main">
              <AcademyThemeBanner
                academyContent={academyContent}
                academyId={academy.id}
                backgroundImage={
                  customThemeOptionsBanner?.backgroundImage
                    ? customThemeOptionsBanner.backgroundImage
                    : academy.backgroundImage
                }
                button={customButtonProps}
                darkenBackgroundImage={
                  customThemeOptionsBanner && _.has(customThemeOptionsBanner, 'darkenBackgroundImage')
                    ? customThemeOptionsBanner.darkenBackgroundImage
                    : themeOptions.darkenBackgroundImage
                }
                description={customThemeOptionsBanner?.description}
                descriptionColor={
                  customThemeOptionsBanner?.descriptionColor
                    ? customThemeOptionsBanner.descriptionColor
                    : themeOptions.descriptionColor
                }
                foregroundImage={customThemeOptionsBanner?.foregroundImage}
                heightStyle={customThemeOptionsBanner?.heightStyle}
                imageAlign={customThemeOptionsBanner?.imageAlign}
                logoHeight={themeOptions.logoHeight}
                noContent={
                  customThemeOptionsBanner && _.has(customThemeOptionsBanner, 'noBannerContent')
                    ? customThemeOptionsBanner.noBannerContent
                    : false
                }
                stats={
                  customThemeOptionsBanner?.stats && _.isArray(customThemeOptionsBanner.stats)
                    ? customThemeOptionsBanner.stats
                    : []
                }
                style={
                  customThemeOptionsBanner?.style && _.isObject(customThemeOptionsBanner.style)
                    ? customThemeOptionsBanner.style
                    : {}
                }
                textAlign={
                  customThemeOptionsBanner?.textAlign ? customThemeOptionsBanner.textAlign : themeOptions.textAlign
                }
                title={customThemeOptionsBanner?.title}
                titleColor={
                  customThemeOptionsBanner?.titleColor ? customThemeOptionsBanner.titleColor : themeOptions.titleColor
                }
              />

              {customThemeOptionsCurrentSubMenu && (
                <AcademyThemeSubMenu
                  adminUser={adminUser}
                  contentManagerEnabled={contentManagerEnabled}
                  certificationsEnabled={certificationsEnabled}
                  collectionsEnabled={collectionsEnabled}
                  currentMenu={customThemeOptionsCurrentSubMenu || APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.TRAININGS}
                  currentUser={currentUser}
                  customPages={academyCustomPages}
                  eventsEnabled={eventsEnabled}
                  myTrainingsEnabled={showMyTrainingsNavigation}
                  pathsEnabled={showPathsNavigation}
                  subnavActiveLinkColor={
                    customThemeOptionsBanner && customThemeOptionsBanner.subnavActiveLinkColor
                      ? customThemeOptionsBanner.subnavActiveLinkColor
                      : themeOptions.subnavActiveLinkColor
                  }
                  subnavAlign={
                    customThemeOptionsBanner && customThemeOptionsBanner.subnavAlign
                      ? customThemeOptionsBanner.subnavAlign
                      : themeOptions.subnavAlign
                  }
                  subnavBackgroundColor={
                    customThemeOptionsBanner && customThemeOptionsBanner.subnavBackgroundColor
                      ? customThemeOptionsBanner.subnavBackgroundColor
                      : themeOptions.subnavBackgroundColor
                  }
                  subnavLinkColor={
                    customThemeOptionsBanner && customThemeOptionsBanner.subnavLinkColor
                      ? customThemeOptionsBanner.subnavLinkColor
                      : themeOptions.subnavLinkColor
                  }
                  themeOptions={themeOptions}
                  pagesOrder={academy.pagesOrder}
                />
              )}

              <AcademyBreadcrumb
                breadcrumbs={customThemeOptionsBreadCrumbs}
                linkColor={themeOptions?.breadCrumbLinkColor}
                padding={customThemeOptionsBreadCrumbs?.padding}
                title={breadcrumbLabel}
              />
              <WrappedComponent
                {...this.props}
                hasBreadcrumbs={customThemeOptionsBreadCrumbs}
                showFilters={showFilters}
                subnavAlign={
                  customThemeOptionsBanner?.subnavAlign
                    ? customThemeOptionsBanner.subnavAlign
                    : themeOptions.subnavAlign
                }
                purchasableContentInfo={customButtonProps.purchasableContentInfo}
              />
            </main>
            <AcademyThemeFooter
              adminUser={adminUser}
              backgroundColor={themeOptions.footerBackgroundColor}
              currentLanguage={userLanguageCode}
              footerLinks={themeOptions.footerLinks}
              footerText={themeOptions.footerText}
              hideFooterLanguagePicker={themeOptions.hideFooterLanguagePicker}
              showFooterCredits={themeOptions.showFooterCredits}
              textColor={themeOptions.footerTextColor}
            />
          </AcademyThemeWrapper>
        </div>
      );
    }
  };

export default withAcademyTheme;

const SkipToMainLink = styled.a`
  position: absolute;
  left: -9999px;
  z-index: 999;
  padding: 1em;
  background-color: black;
  color: white;
  opacity: 0;

  &:focus {
    transform: translateX(-50%);
    left: 50%;
    opacity: 1;
  }
`;
